import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import UndoIcon from '@mui/icons-material/Undo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const LIGHT_PALETTE = {
  dialogBg: '#f5f7fa',
  surface: '#fff',
  inputBg: '#fff',
  hover: 'rgba(25, 118, 210, 0.04)',
  border: '#dde3ec',
  accent: '#1976d2',
  accentHover: '#1565c0',
  accentSoft: 'rgba(25, 118, 210, 0.08)',
  title: '#1a253c',
  text: '#212529',
  textSecondary: '#6c757d',
  mutedSoft: 'rgba(108, 117, 125, 0.1)',
  open: '#2e7d32',
  openSoft: 'rgba(46, 125, 50, 0.07)',
  restricted: '#d32f2f',
  restrictedSoft: 'rgba(211, 47, 47, 0.06)',
  warning: '#ed6c02',
  warningSoft: 'rgba(237, 108, 2, 0.08)',
  shadow: '0 10px 30px rgba(0, 0, 0, 0.12)'
};

// Couleurs nuit du document ouvert (core)
const DARK_CORE_PALETTE = {
  dialogBg: '#1a252f',
  surface: '#22303e',
  inputBg: '#1a252f',
  hover: 'rgba(79, 195, 247, 0.08)',
  border: '#2d3f4c',
  accent: '#4fc3f7',
  accentHover: '#29b6f6',
  accentSoft: 'rgba(79, 195, 247, 0.15)',
  title: '#4fc3f7',
  text: '#ecf0f1',
  textSecondary: '#95a5a6',
  mutedSoft: 'rgba(149, 165, 166, 0.15)',
  open: '#4ecdc4',
  openSoft: 'rgba(78, 205, 196, 0.12)',
  restricted: '#ff6b6b',
  restrictedSoft: 'rgba(244, 67, 54, 0.12)',
  warning: '#ffb74d',
  warningSoft: 'rgba(255, 183, 77, 0.12)',
  shadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
  buttonText: '#000',
  buttonDisabledBg: 'rgba(79, 195, 247, 0.2)',
  buttonDisabledText: 'rgba(0, 0, 0, 0.5)',
  cancelText: '#bdc3c7'
};

// Couleurs nuit des pages de l'application (tableau de bord, compte…)
const DARK_APP_PALETTE = {
  ...DARK_CORE_PALETTE,
  dialogBg: '#1a253c',
  surface: '#233044',
  inputBg: '#1a253c',
  hover: 'rgba(144, 202, 249, 0.08)',
  border: 'rgba(144, 202, 249, 0.18)',
  accent: '#90caf9',
  accentHover: '#64b5f6',
  accentSoft: 'rgba(144, 202, 249, 0.15)',
  title: '#90caf9',
  text: '#fff',
  textSecondary: '#b0bec5',
  mutedSoft: 'rgba(176, 190, 197, 0.15)',
  buttonText: '#1a253c',
  buttonDisabledBg: 'rgba(144, 202, 249, 0.3)',
  buttonDisabledText: 'rgba(26, 37, 60, 0.6)',
  cancelText: '#b0bec5'
};

export const getAccessPalette = (isDark, variant = 'core') => {
  if (!isDark) return LIGHT_PALETTE;
  return variant === 'app' ? DARK_APP_PALETTE : DARK_CORE_PALETTE;
};

export const normalizeSearch = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

export const plural = (count, singular, pluralForm = `${singular}s`) => `${count} ${count > 1 ? pluralForm : singular}`;

// Même lecture que la page d'administration « Restriction par défaut » (ancien format booléen compris)
export const getRestrictionMode = (settings) => {
  if (settings?.restrictionMode) return settings.restrictionMode;
  return settings?.defaultRestricted === true ? 'default' : 'none';
};

export const personName = (person) => `${(person.lastname || '').toUpperCase()} ${person.firstname || ''}`.trim();

export const personDetails = (person) => [person.rank, person.service].filter(Boolean).join(' · ');

export const personInitials = (person) =>
  `${person.lastname?.[0] || ''}${person.firstname?.[0] || ''}`.toUpperCase() || '?';

export const getSectionTitleSx = (c, isDark) => ({
  color: c.title,
  fontWeight: isDark ? 600 : 700,
  textTransform: isDark ? 'none' : 'uppercase',
  letterSpacing: isDark ? 'normal' : '0.03em',
  fontSize: isDark ? '0.875rem' : '0.72rem'
});

export const getInputSx = (c, isDark) => ({
  '& .MuiInputBase-root': {
    backgroundColor: c.inputBg,
    color: c.text
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: isDark ? c.border : undefined },
    '&:hover fieldset': { borderColor: isDark ? c.accent : undefined },
    '&.Mui-focused fieldset': { borderColor: c.accent },
    '&.Mui-error fieldset': { borderColor: c.restricted }
  },
  '& .MuiInputLabel-root': { color: c.textSecondary },
  '& .MuiInputLabel-root.Mui-focused': { color: c.accent },
  '& .MuiInputLabel-root.Mui-error': { color: c.restricted },
  '& .MuiFormHelperText-root': { color: c.textSecondary },
  '& .MuiFormHelperText-root.Mui-error': { color: c.restricted },
  '& .MuiInputBase-input::placeholder': { color: c.textSecondary, opacity: 1 },
  '& .MuiInputBase-root.Mui-disabled': { opacity: isDark ? 0.7 : 1 },
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: c.textSecondary },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.accent },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: c.textSecondary }
});

export const getAutocompleteSlotProps = (c) => ({
  paper: {
    sx: {
      backgroundColor: c.surface,
      backgroundImage: 'none',
      color: c.text,
      border: `1px solid ${c.border}`,
      borderRadius: '10px',
      boxShadow: c.shadow,
      '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
        backgroundColor: c.accentSoft
      },
      '& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: c.accentSoft },
      '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { color: c.textSecondary }
    }
  },
  listbox: { sx: { maxHeight: 280 } }
});

export const getPrimaryButtonSx = (c, isDark) => (isDark ? {
  backgroundColor: c.accent,
  color: c.buttonText,
  '&:hover': { backgroundColor: c.accentHover },
  '&.Mui-disabled': { backgroundColor: c.buttonDisabledBg, color: c.buttonDisabledText }
} : {});

const toneColors = (c, tone) => ({
  accent: [c.accentSoft, c.accent],
  open: [c.openSoft, c.open],
  restricted: [c.restrictedSoft, c.restricted],
  warning: [c.warningSoft, c.warning],
  muted: [c.mutedSoft, c.textSecondary]
}[tone] || [c.accentSoft, c.accent]);

const safeContrastText = (muiTheme, color) => {
  try {
    return muiTheme.palette.getContrastText(color);
  } catch {
    return '#fff';
  }
};

export function AccessTag({ c, label, tone = 'accent', icon }) {
  const [soft, strong] = toneColors(c, tone);
  return (
    <Chip
      label={label}
      icon={icon}
      size="small"
      sx={{
        height: 20,
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: soft,
        color: strong,
        '& .MuiChip-label': { px: 0.75 },
        '& .MuiChip-icon': { color: strong, fontSize: 13, ml: 0.75, mr: -0.25 }
      }}
    />
  );
}

export function AccessBanner({ c, tone, icon: Icon, title, children, sx }) {
  const [soft, strong] = toneColors(c, tone);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderRadius: '10px',
        backgroundColor: soft,
        borderLeft: `3px solid ${strong}`,
        ...sx
      }}
    >
      <Icon sx={{ fontSize: 20, color: strong, mt: 0.1 }} />
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography variant="body2" sx={{ fontWeight: 700, color: c.text }}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: title ? c.textSecondary : c.text, fontSize: '0.82rem' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

// Carte de choix exclusive (type de PV, visibilité…) ; sans description, elle passe en version compacte
export function ChoiceCard({
  c,
  selected,
  onClick,
  icon: Icon,
  title,
  description,
  tone = 'accent',
  badge,
  disabled = false,
  lockedReason = ''
}) {
  const [soft, strong] = toneColors(c, tone);
  const locked = Boolean(lockedReason);
  const compact = !description;
  const lockedTag = <AccessTag c={c} label="Interdit par l'administrateur" tone="warning" icon={<BlockIcon />} />;

  return (
    <Tooltip title={lockedReason} placement="top" arrow>
      <Box sx={{ flex: 1, display: 'flex', minWidth: 0, cursor: locked ? 'not-allowed' : 'pointer' }}>
        <ButtonBase
          focusRipple
          disabled={disabled || locked}
          onClick={onClick}
          aria-pressed={selected}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: compact ? 'center' : 'flex-start',
            justifyContent: 'flex-start',
            gap: compact ? 1.25 : 1.5,
            p: compact ? 1.25 : 1.75,
            pl: Icon ? undefined : 1.75,
            minHeight: compact ? 54 : undefined,
            textAlign: 'left',
            borderRadius: '12px',
            border: `1px solid ${selected ? strong : c.border}`,
            boxShadow: selected ? `inset 0 0 0 1px ${strong}` : 'none',
            backgroundColor: selected ? soft : c.surface,
            opacity: locked ? 0.55 : 1,
            transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': selected || locked ? undefined : { borderColor: strong, backgroundColor: c.hover },
            '&:focus-visible': { outline: `2px solid ${strong}`, outlineOffset: '2px' }
          }}
        >
          {Icon && (
            <Box
              sx={{
                width: compact ? 32 : 40,
                height: compact ? 32 : 40,
                borderRadius: compact ? '8px' : '10px',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                backgroundColor: soft,
                color: strong
              }}
            >
              <Icon sx={{ fontSize: compact ? 19 : 24 }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: compact ? 0 : 0.25 }}>
              <Typography sx={{ fontWeight: 700, color: selected ? strong : c.text, fontSize: compact ? '0.9rem' : '0.95rem' }}>
                {title}
              </Typography>
              {badge && <AccessTag c={c} label={badge} tone="muted" />}
              {locked && compact && lockedTag}
            </Box>
            {!compact && (
              <Typography variant="body2" sx={{ color: c.textSecondary, fontSize: '0.8rem', lineHeight: 1.45 }}>
                {description}
              </Typography>
            )}
            {locked && !compact && <Box sx={{ mt: 0.75 }}>{lockedTag}</Box>}
          </Box>
          <CheckCircleIcon
            sx={{
              fontSize: 20,
              flexShrink: 0,
              color: strong,
              opacity: selected ? 1 : 0,
              transform: selected ? 'scale(1)' : 'scale(0.6)',
              transition: 'opacity 0.2s ease, transform 0.2s ease'
            }}
          />
        </ButtonBase>
      </Box>
    </Tooltip>
  );
}

// currentType : état enregistré du document (absent lors d'une création)
export function VisibilityChoice({
  c,
  value,
  onChange,
  publicForbidden = false,
  currentType,
  disabled = false,
  compact = false,
  direction = { xs: 'column', sm: 'row' }
}) {
  const publicLocked = publicForbidden && currentType !== 'public';

  return (
    <Box sx={{ display: 'flex', flexDirection: direction, gap: 1.25 }}>
      <ChoiceCard
        c={c}
        selected={value === 'public'}
        onClick={() => onChange('public')}
        icon={LockOpenIcon}
        title="Non-restreint"
        description={compact ? '' : 'Tous les utilisateurs peuvent consulter et modifier ce document.'}
        tone="open"
        badge={currentType === 'public' ? 'Actuel' : null}
        disabled={disabled}
        lockedReason={publicLocked ? 'L\'administrateur impose que tous les documents soient restreints' : ''}
      />
      <ChoiceCard
        c={c}
        selected={value === 'restricted'}
        onClick={() => onChange('restricted')}
        icon={LockIcon}
        title="Restreint"
        description={compact ? '' : 'Seules les personnes autorisées peuvent consulter et modifier ce document.'}
        tone="restricted"
        badge={currentType === 'restricted' ? 'Actuel' : null}
        disabled={disabled}
      />
    </Box>
  );
}

// added / removed : différences avec l'état enregistré, affichées sous forme d'étiquettes
export function AuthorizedPeople({
  c,
  isDark,
  users,
  loading = false,
  value,
  onChange,
  me,
  lockSelf = true,
  creator,
  added = [],
  removed = [],
  disabled = false,
  showModeratorNote = true,
  sx
}) {
  const muiTheme = useTheme();
  const [searchValue, setSearchValue] = useState('');

  const usersByMatricule = useMemo(() => new Map(users.map((person) => [person.matricule, person])), [users]);
  const valueSet = useMemo(() => new Set(value), [value]);
  const addableUsers = useMemo(
    () => users
      .filter((person) => !valueSet.has(person.matricule) && !person.isBlocked)
      .sort((a, b) => personName(a).localeCompare(personName(b), 'fr')),
    [users, valueSet]
  );

  const describe = (matricule) => {
    const person = usersByMatricule.get(matricule);
    if (!person) {
      return {
        name: 'Utilisateur inconnu',
        details: `Matricule ${matricule}`,
        initials: '?',
        color: '#9e9e9e',
        moderator: false
      };
    }
    return {
      name: personName(person),
      details: personDetails(person),
      initials: personInitials(person),
      color: person.nicknameColor || '#9e9e9e',
      moderator: Boolean(person.moderator)
    };
  };

  const sortPeople = (list) => [...list].sort((a, b) => {
    if (a === me) return -1;
    if (b === me) return 1;
    return describe(a).name.localeCompare(describe(b).name, 'fr');
  });

  const addUser = (matricule) => {
    if (!valueSet.has(matricule)) onChange([...value, matricule]);
  };

  const removeUser = (matricule) => {
    onChange(value.filter((item) => item !== matricule));
  };

  const renderPersonRow = (matricule, isRemoved) => {
    const person = describe(matricule);
    const isMe = matricule === me;
    const selfLocked = isMe && lockSelf;
    const tags = [
      isMe && { label: 'Vous', tone: 'accent' },
      creator && matricule === creator && { label: 'Créateur', tone: 'muted' },
      person.moderator && { label: 'Modérateur', tone: 'muted' },
      !isRemoved && added.includes(matricule) && { label: 'Ajouté', tone: 'open' },
      isRemoved && { label: 'Retiré', tone: 'restricted' }
    ].filter(Boolean);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 0.875,
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: c.hover }
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.78rem',
            fontWeight: 700,
            bgcolor: person.color,
            color: safeContrastText(muiTheme, person.color),
            opacity: isRemoved ? 0.45 : 1
          }}
        >
          {person.initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isRemoved ? c.textSecondary : c.text,
                textDecoration: isRemoved ? 'line-through' : 'none'
              }}
            >
              {person.name}
            </Typography>
            {tags.map((tag) => <AccessTag key={tag.label} c={c} {...tag} />)}
          </Box>
          {person.details && (
            <Typography
              variant="caption"
              noWrap
              component="div"
              sx={{ color: c.textSecondary, opacity: isRemoved ? 0.7 : 1 }}
            >
              {person.details}
            </Typography>
          )}
        </Box>
        {isRemoved ? (
          <Tooltip title="Rétablir l'accès">
            <IconButton
              size="small"
              onClick={() => addUser(matricule)}
              disabled={disabled}
              sx={{ color: c.accent, '&:hover': { backgroundColor: c.accentSoft } }}
            >
              <UndoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={selfLocked ? 'Vous ne pouvez pas vous retirer vous-même' : 'Retirer l\'accès'}>
            <span>
              <IconButton
                size="small"
                onClick={() => removeUser(matricule)}
                disabled={selfLocked || disabled}
                sx={{
                  color: c.textSecondary,
                  '&:hover': { color: c.restricted, backgroundColor: c.restrictedSoft },
                  '&.Mui-disabled': { color: c.textSecondary, opacity: 0.35 }
                }}
              >
                <PersonRemoveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const rows = [
    ...sortPeople(value).map((matricule) => [matricule, false]),
    ...sortPeople(removed.filter((matricule) => !valueSet.has(matricule))).map((matricule) => [matricule, true])
  ];

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
        <Typography variant="body2" sx={getSectionTitleSx(c, isDark)}>
          Personnes autorisées
        </Typography>
        <Typography variant="body2" sx={{ color: c.textSecondary, fontWeight: 500 }}>
          ({value.length})
        </Typography>
      </Box>

      <Autocomplete
        options={addableUsers}
        value={null}
        inputValue={searchValue}
        onInputChange={(_, input) => setSearchValue(input)}
        onChange={(_, person) => {
          if (person) addUser(person.matricule);
          setSearchValue('');
        }}
        autoHighlight
        disabled={loading || disabled}
        loading={loading}
        loadingText="Chargement des utilisateurs…"
        noOptionsText="Aucun utilisateur trouvé"
        getOptionLabel={(option) => personName(option)}
        isOptionEqualToValue={(option, selected) => option.matricule === selected.matricule}
        filterOptions={(options, { inputValue }) => {
          const query = normalizeSearch(inputValue);
          if (!query) return options;
          return options.filter((option) =>
            normalizeSearch(`${option.lastname} ${option.firstname} ${option.rank} ${option.service} ${option.matricule}`).includes(query)
          );
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const color = option.nicknameColor || '#9e9e9e';
          return (
            <Box component="li" key={option.matricule} {...optionProps} sx={{ gap: 1.25 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: color,
                  color: safeContrastText(muiTheme, color)
                }}
              >
                {personInitials(option)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: c.text }}>
                  {personName(option)}
                </Typography>
                <Typography variant="caption" noWrap component="div" sx={{ color: c.textSecondary }}>
                  {personDetails(option)}
                </Typography>
              </Box>
            </Box>
          );
        }}
        slotProps={getAutocompleteSlotProps(c)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Ajouter une personne : nom, grade, service…"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonAddAlt1Icon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={getInputSx(c, isDark)}
          />
        )}
      />

      <Paper
        elevation={0}
        sx={{
          mt: 1.25,
          backgroundColor: c.surface,
          backgroundImage: 'none',
          border: `1px solid ${c.border}`,
          borderRadius: '10px',
          overflow: 'hidden'
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
            <CircularProgress size={22} sx={{ color: c.accent }} />
          </Box>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" sx={{ color: c.textSecondary, fontStyle: 'italic', textAlign: 'center', py: 2.5 }}>
            Aucune personne autorisée : ajoutez au moins une personne.
          </Typography>
        )}
        {!loading && rows.map(([matricule, isRemoved], index) => (
          <Box key={matricule}>
            {index > 0 && <Divider sx={{ borderColor: c.border }} />}
            {renderPersonRow(matricule, isRemoved)}
          </Box>
        ))}
      </Paper>

      {showModeratorNote && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, color: c.textSecondary }}>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            Les modérateurs gardent l'accès à tous les documents, même restreints.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
